/**
 * DashboardPage — the read-only analytics tableau (Manager surface, AN-0/AN-2). It reads
 * GET /analytics/tableau (audit-on-read happens server-side on every read) and renders:
 *   - the live current-day KPI tiles (TAT / volume / revenue / qc / reagent) with rapport tone bands;
 *   - a period bascule (YTD / 3-mo / 12-mo) that windows the Recharts trend line over the snapshot
 *     history, with an indicator picker;
 *   - the referral federation tile — shown only when the server federated it (hidden when referral is
 *     unmounted, D-28).
 *
 * Drives the full 5-state DoD (vide / chargement / erreur / hors-ligne / sans-droit) PLUS a sixth
 * "not available on this install" state for the cloud-only-degrade posture (route 404/501).
 */
import { useMemo, useState } from "react";
import { Button, Card, EmptyState, ErrorState, ForbiddenState, LoadingState, OfflineState, Select } from "@/ui";
import { useAuthStore, useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { ANALYTICS_CAPS } from "../caps";
import { isModuleAbsent, useDashboard, useLabId } from "../api";
import { bandCapFor, metaFor } from "../lib";
import type { LiveMetric, MetricType, Period } from "../types";
import { PageHeader } from "../components/PageHeader";
import { PeriodSwitch } from "../components/PeriodSwitch";
import { KpiTile } from "../components/KpiTile";
import { TrendChart } from "../components/TrendChart";
import { ReferralTile } from "../components/ReferralTile";
import { ExportMenu } from "../components/ExportMenu";
import { DisciplineTatTable } from "../components/DisciplineTatTable";

export function DashboardPage() {
  const { t } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canView = useCan(ANALYTICS_CAPS.tableauView);
  const caps = useAuthStore((s) => s.capabilities); // NOVA-034 — the actor's effective set, for per-band tile gating

  const [period, setPeriod] = useState<Period>("12mo");
  const [trendMetric, setTrendMetric] = useState<MetricType>("volume_demandes");

  const dash = useDashboard(labId, canView && online);
  const data = dash.data;

  // NOVA-034 — the server is the band trust boundary: the rows that arrived are ALREADY scoped to the
  // actor's bands. The client gate is belt-and-braces ONLY for a non-Manager actor (no tableau.view): a
  // full-view Manager (tableau.view) trusts the server payload as-sent, so a band tile is never double-
  // hidden. `inBand` therefore short-circuits to true when the actor holds tableau.view.
  const inBand = useMemo(() => {
    const full = caps.has(ANALYTICS_CAPS.tableauView);
    return (metricType: MetricType) => full || caps.has(bandCapFor(metricType));
  }, [caps]);

  // The lab-wide KPI tiles: rows with no discipline (the per-discipline TAT fan-out goes to its own table).
  const tiles = useMemo<LiveMetric[]>(() => {
    const live = (data?.live_today ?? []).filter((m) => m.discipline_code == null && inBand(m.metric_type));
    return [...live].sort((a, b) => metaFor(a.metric_type).order - metaFor(b.metric_type).order);
  }, [data, inBand]);

  // NOVA-037 — the per-discipline TAT rows (tat_signoff with a discipline_code), gated on the tat band.
  const disciplineTat = useMemo<LiveMetric[]>(() => {
    if (!inBand("tat_signoff")) return [];
    return (data?.live_today ?? [])
      .filter((m) => m.metric_type === "tat_signoff" && m.discipline_code != null)
      .sort((a, b) => String(a.discipline_code).localeCompare(String(b.discipline_code)));
  }, [data, inBand]);

  // The metrics that have any LAB-WIDE history → the trend picker options (fall back to the live tiles).
  // Per-discipline series are excluded from the picker (the discipline view is the table); each option is
  // band-gated so the picker never offers a series the actor cannot read (NOVA-034).
  const trendOptions = useMemo(() => {
    const fromHistory = new Set(
      (data?.history ?? []).filter((h) => h.discipline_code == null).map((h) => h.metric_type),
    );
    const keys = fromHistory.size > 0 ? [...fromHistory] : tiles.map((m) => m.metric_type);
    return keys
      .filter((k) => inBand(k))
      .map((k) => ({ value: String(k), label: t(`analytics.metric.${k}`) }))
      .sort((a, b) => metaFor(a.value).order - metaFor(b.value).order);
  }, [data, tiles, inBand, t]);

  // Keep the trend selection valid against the available options.
  const activeTrend: MetricType = useMemo(() => {
    if (trendOptions.some((o) => o.value === trendMetric)) return trendMetric;
    return (trendOptions[0]?.value as MetricType) ?? "volume_demandes";
  }, [trendOptions, trendMetric]);

  const header = (
    <PageHeader
      title={t("analytics.title")}
      subtitle={t("analytics.subtitle")}
      actions={
        data ? (
          <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-3)", flexWrap: "wrap" }}>
            {/* NOVA-038 — the export download (gated on tableau.view; the server band-scopes the rows). */}
            <ExportMenu />
            <Button variant="ghost" size="sm" onClick={() => void dash.refetch()} disabled={dash.isFetching}>
              {t("analytics.action.refresh")}
            </Button>
          </div>
        ) : undefined
      }
    />
  );

  // ── 5-state DoD (+ cloud-absent) ─────────────────────────────────────────────
  if (!canView) return <ForbiddenState requiredCapabilities={[ANALYTICS_CAPS.tableauView]} />;

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void dash.refetch()} cached={!!data} />
      </Screen>
    );
  }
  if (dash.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={5} />
      </Screen>
    );
  }
  if (dash.isError) {
    // Cloud-only degrade: the module's route is not mounted on this install → clean "unavailable" state.
    if (isModuleAbsent(dash.error)) {
      return (
        <Screen>
          {header}
          <EmptyState glyph="☁" title={t("analytics.absent.title")} description={t("analytics.absent.desc")} />
        </Screen>
      );
    }
    return (
      <Screen>
        {header}
        <ErrorState message={dash.error.message} code={dash.error.code} onRetry={() => void dash.refetch()} />
      </Screen>
    );
  }
  if (tiles.length === 0 && (data?.history.length ?? 0) === 0) {
    return (
      <Screen>
        {header}
        <EmptyState title={t("analytics.empty.title")} description={t("analytics.empty.desc")} />
      </Screen>
    );
  }

  return (
    <Screen>
      {header}

      <p style={{ margin: 0, fontSize: "var(--nv-fs-caption)", color: "var(--nv-steel)" }}>
        {t("analytics.readonly.note")}
      </p>

      <Card title={t("analytics.tiles.live")}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "var(--nv-s-3)",
          }}
        >
          {tiles.map((m) => (
            <KpiTile key={String(m.metric_type)} metric={m} meta={metaFor(m.metric_type)} />
          ))}
        </div>
      </Card>

      <Card
        title={t("analytics.trend.title")}
        actions={<PeriodSwitch value={period} onChange={setPeriod} />}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
          <div style={{ maxWidth: 320 }}>
            <Select
              aria-label={t("analytics.trend.pick")}
              value={String(activeTrend)}
              onChange={(e) => setTrendMetric(e.target.value as MetricType)}
              options={trendOptions}
            />
          </div>
          <TrendChart history={data?.history ?? []} meta={metaFor(activeTrend)} period={period} />
        </div>
      </Card>

      {/* NOVA-037 — the per-discipline TAT breakdown (shown only when the server fanned out at least one). */}
      {disciplineTat.length > 0 && <DisciplineTatTable rows={disciplineTat} />}

      {data?.referral && <ReferralTile referral={data.referral} />}
    </Screen>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>;
}
